import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './style.css';
import { noop } from 'functions/noop';
import { NewResultsAnimation } from 'components/NewResultsAnimation';
import { FullScreenPortal } from 'components/FullScreenPortal/FullScreenPortal';
import Radio from 'new-ui/Components/Form/Radio';
import Button from 'new-ui/Components/Button';
import { useTranslation } from 'utils/modifiedTranslation';
import { getSurvey, setSurvey } from 'modules/survey/api';
import { SurveyAnswerKeys, SurveyTypes } from 'modules/survey/surveyEnums';
import app from 'new-ui/app';
import ArrowButton from '../ArrowButton';
import TreatmentIntensity from './TreatmentIntensity';
import AssistanceButton from '../AssistanceButton';
import WidgetBtn from '../Button';
import { FEED_GA } from '../constants';

const ExperienceWidgetV2 = ({
  onAddExperance, onChangeSelectedDrug, selectedDrug, onClickPreviousTreatment, onReady,
}) => {
  const { t } = useTranslation();
  const [reset, setReset] = useState(0);
  const [sideEffects, setSideEffects] = useState(null);
  const [drugs, setDrugs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentDrugIndex, setCurrentDrugIndex] = useState(0);
  const [aggression, setAggression] = useState(null);
  const [sideEffect, setSideEffect] = useState(null);
  const [freeText, setFreeText] = useState('');
  const [currentDrug, setCurrentDrug] = useState(null);
  const [answers, _setAnswers] = useState([]);
  const [patientsCount, setPatientsCount] = useState(0);
  const [isSuccessFeedbackAnimationShown, setIsSuccessFeedbackAnimationShown] = useState(false);

  useEffect(() => {
    app.sendGoogleAnalyticsEvent(FEED_GA.category, FEED_GA.events.surveyViewed, {
      widget: 'treatment survey',
    });
    const fetch = async () => {
      setIsLoading(true);
      const experiences = await getSurvey(SurveyTypes.treatmentExperience, app.user.info.user_wix_id);
      const nextSideEffects = experiences.data.data.sideEffects.map(({ key, id }) => ({
        title: t(`side_effects.${key}`),
        name: key,
        value: id,
      }));
      const { drugs: nextDrugs } = experiences.data.data;

      setSideEffects(nextSideEffects);
      setDrugs(nextDrugs);
      setPatientsCount(experiences.data.data.patients_with_same_indication);

      const drug = selectedDrug || nextDrugs[nextDrugs.length - 1];

      setCurrentDrug(drug);

      _setAnswers(experiences.data.answers || []);
      setIsLoading(false);
      onReady(true);
    };
    fetch();
    // eslint-disable-next-line
  }, []);

  const radioApi = useRef({});

  const triggerReset = () => {
    setReset((prev) => prev + 1);
    setAggression(null);
    setFreeText('');
    setSideEffect(null);
    radioApi.current.setValue(null);
  };

  useEffect(() => {
    const drug = selectedDrug || drugs[currentDrugIndex];
    setCurrentDrug(drug);
    // eslint-disable-next-line
    },[selectedDrug])

  useEffect(() => {
    const drug = drugs[currentDrugIndex];
    if (drug) {
      setCurrentDrug(drug);
      onChangeSelectedDrug(drug);
    }
    // eslint-disable-next-line
    },[currentDrugIndex])

  useEffect(() => {
    if (isSuccessFeedbackAnimationShown) {
      const timeoutId = setTimeout(() => {
        setIsSuccessFeedbackAnimationShown(false);
      }, 2000);
      return () => {
        clearTimeout(timeoutId);
      };
    }
    return noop;
  }, [isSuccessFeedbackAnimationShown]);

  const onBtnClick = async () => {
    onClickPreviousTreatment();
  };

  const setAnswers = (surveyKey, key, values = []) => {
    let i = answers.length;

    // If the key is not 'side_effects', remove previous answers for that key.
    while (i > 0) {
      i -= 1;
      const answer = answers[i];
      if (answer.surveyKey === surveyKey && answer.key === key && key !== SurveyAnswerKeys.sideEffects) {
        answers.splice(i, 1);
      }
    }

    if (typeof values === 'string') {
      // If key is 'side_effects', allow multiple values by pushing without removing
      if (key === SurveyAnswerKeys.sideEffects) {
        answers.push({ surveyKey, key, value: values });
      } else {
        answers.push({ surveyKey, key, value: values });
      }
    } else {
      values.forEach((value) => {
        answers.push({ surveyKey, key, value });
      });
    }
  };

  const onClickDone = async () => {
    if (sideEffect) {
      for (const effect of sideEffect) {
        setAnswers(currentDrug.name, SurveyAnswerKeys.sideEffects, effect.name);
      }
    }
    if (aggression) {
      setAnswers(currentDrug.name, SurveyAnswerKeys.aggression, aggression);
    }
    if (freeText) {
      setAnswers(currentDrug.name, SurveyAnswerKeys.sideEffectsFreeText, freeText);
    }

    app.showPreloader();
    await setSurvey(SurveyTypes.treatmentExperience, answers, app.user.info.user_wix_id);
    triggerReset();

    app.hidePreloader();
    setIsSuccessFeedbackAnimationShown(true);
    onAddExperance(currentDrug);
  };

  const onChangeAggression = (value) => {
    setAggression(value);
  };

  const onChangeSideEffect = (values) => {
    const effects = [];
    for (const value of values) {
      const effect = sideEffects.find((e) => e.value === value);
      effects.push(effect);
    }
    setSideEffect(effects);
  };

  const onChangeSideEffectRadio = (value) => {
    setSideEffect(value);
  };

  const onChangeFreeText = (event) => {
    setFreeText(event.target.value);
  };

  const onNext = () => {
    setCurrentDrugIndex((prevIndex) => (prevIndex + 1) % drugs.length);
    app.sendGoogleAnalyticsEvent(FEED_GA.category, FEED_GA.events.surveyInteracted, {
      widget: 'treatment survey',
    });
  };

  const onPrev = () => {
    setCurrentDrugIndex((prevIndex) => (prevIndex - 1 + drugs.length) % drugs.length);
    app.sendGoogleAnalyticsEvent(FEED_GA.category, FEED_GA.events.surveyInteracted, {
      widget: 'treatment survey',
    });
  };

  const getDrugName = () => {
    let names = '';
    if (currentDrug) {
      names = currentDrug.name.split('+').map((name) => t(`questionnaire.questions.drugs_received.${name}`));
      names = names.join(' + ');
    }
    return names;
  };

  return (
    <>
      {!isLoading && (
        <div className="experience-v2-widget">
          <div className={`widget-header ${drugs.length > 0 ? 'arrows' : ''}`}>
            <ArrowButton direction="left" onClick={onPrev} />
            <ArrowButton direction="right" onClick={onNext} />
            <div className="details">
              <div className="label">{getDrugName()}</div>
            </div>
          </div>
          <div className="widget-body">
            <h2 className="title">{t('experience_widget.title')}</h2>
            <h3 className="sub-title">
              {`${t('experience_widget.help_patients.p1')} ${patientsCount} ${t('experience_widget.help_patients.p2')}`}
            </h3>

            {drugs.length > 1 && (
              <>
                <ArrowButton direction="left" onClick={onPrev} />
                <ArrowButton direction="right" onClick={onNext} />
              </>
            )}

            <TreatmentIntensity
              sideEffects={sideEffects}
              onChangeAggression={onChangeAggression}
              onChangeSideEffect={onChangeSideEffect}
              reset={reset}
            />

            <div className="radios">
              <Radio
                options={[{ value: 'no_sideeffects', title: t('experience_widget.no_side_effect') }]}
                value={null}
                api={radioApi.current}
                onChange={onChangeSideEffectRadio}
              />
            </div>

            <div className="overview-container">
              <h2 className="title">{t('experience_widget.overview.title')}</h2>
              <textarea value={freeText} onChange={onChangeFreeText} placeholder={t('experience_widget.textarea.placeholder')} />
            </div>

            <div className="actions">
              <Button
                backgroundColor="#FFF"
                color="#FF8C69"
                width="250px"
                className="btn btn-done"
                title={t('treatment_widget.actions.done.button')}
                action={onClickDone}
              />
              <WidgetBtn title={t('experience_widget.actions.add_treatment.button')} onClick={onBtnClick} />
            </div>
          </div>
          <div className="widget-footer">
            <AssistanceButton title={t('experience_widget.actions.assistance.button')} onClick={() => {}} />
          </div>
        </div>
      )}
      {isSuccessFeedbackAnimationShown ? (
        <FullScreenPortal nonePointerEvents>
          <NewResultsAnimation />
        </FullScreenPortal>
      ) : null}
    </>
  );
};

ExperienceWidgetV2.propTypes = {
  onReady: PropTypes.func.isRequired,
  onAddExperance: PropTypes.func.isRequired,
  onChangeSelectedDrug: PropTypes.func.isRequired,
  selectedDrug: PropTypes.string,
  onClickPreviousTreatment: PropTypes.func.isRequired,
};

export default ExperienceWidgetV2;
